import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Box, Typography } from '@mui/material';
import AutocompleteCDS from '@sharedCR/coreDesignSystem/form/AutocompleteCDS';
import DataFieldCDS from '@sharedCR/coreDesignSystem/form/DataFieldCDS';
import DateFieldCDS from '@sharedCR/coreDesignSystem/form/DateFieldCDS';
import TextFieldCDS from '@sharedCR/coreDesignSystem/form/TextFieldCDS';
import { servicingApi } from 'app/services/api';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { FieldInputType } from 'constants/fields';
import useForm from 'hooks/useForm';
import { logger } from 'utils/logger';

import {
  createServicingPaymentAction,
  updateServicingPaymentAction,
} from 'store/loanApplications/actions';

import {
  PAYMENT_METHOD_OPTIONS,
  PAYMENT_STATUS_OPTIONS,
  ScheduleStatusEnum,
} from '../../Servicing.constants';
import { warningBoxStyles } from '../../Servicing.styles';
import { buildPaymentPayload, formatDateValue } from '../../Servicing.utils';
import { PaymentHistoryTestIds } from '../PaymentHistory.testIds';

import {
  BREAKDOWN_DEBOUNCE_MS,
  OBSERVABLE_FIELDS,
  paymentSchema,
  PERCENTAGE,
} from './PaymentDrawerContent.constants';
import { formFieldsStyles, rowStyles } from './PaymentDrawerContent.styles';
import { getInitialFormValues, parseAmount } from './PaymentDrawerContent.utils';

const PaymentDrawerContent = ({
  setIsFormChanged,
  isSaving,
  setIsLoading,
  setIsSavingAllowed,
  additionalProps: { payment, paymentSchedule = [], applicationId, onSuccess },
  setOnSave,
}) => {
  const dispatch = useDispatch();
  const isEditing = !!payment;

  const unpaidScheduleOptions = useMemo(
    () =>
      paymentSchedule
        .filter(row => row.status !== ScheduleStatusEnum.PAID)
        .map(row => ({
          value: row.dueDate,
          name: formatDateValue(row.dueDate),
          principalDue: row.principalDue,
          interestDue: row.interestDue,
          escrowDue: row.escrowDue,
          totalDue: row.totalDue,
        })),
    [paymentSchedule],
  );

  const initialFormValues = useMemo(() => getInitialFormValues(payment), [payment]);

  const {
    form,
    handleChange,
    isFormChanged,
    setInForm,
    setMultipleInForm,
    setInitialFormValues,
    setForm,
    setSchema,
    validateForm,
    validationErrors,
  } = useForm(initialFormValues, {}, OBSERVABLE_FIELDS);

  useEffect(() => {
    setSchema(paymentSchema);
  }, [setSchema]);

  useEffect(() => {
    setIsFormChanged(isFormChanged);
  }, [isFormChanged, setIsFormChanged]);

  useEffect(() => {
    setInitialFormValues(initialFormValues);
    setForm(initialFormValues);
  }, [initialFormValues, setInitialFormValues, setForm]);

  const onMethodChange = useCallback(
    (_, selectedOption) => {
      setInForm('paymentMethod', selectedOption);
    },
    [setInForm],
  );

  const onStatusChange = useCallback(
    (_, selectedOption) => {
      setInForm('paymentStatus', selectedOption);
    },
    [setInForm],
  );

  const onPaymentDateChange = useCallback(
    value => {
      setInForm('paymentDate', value);
    },
    [setInForm],
  );

  const onEffectiveDateChange = useCallback(
    value => {
      setInForm('effectiveDate', value);
    },
    [setInForm],
  );

  const [isLoadingBreakdown, setIsLoadingBreakdown] = useState(false);

  const fetchBreakdown = useCallback(
    async (amount, dueDate, lateFee) => {
      setIsLoadingBreakdown(true);
      setIsLoading(true);

      try {
        const { data } = await servicingApi.getPaymentBreakdown({
          applicationId,
          paymentDate: form.effectiveDate || form.paymentDate,
          dueDate,
          amount,
          lateFee: parseAmount(lateFee) || null,
        });

        setForm(prev => ({
          ...prev,
          principalAmount: String(data.suggestedPrincipal ?? 0),
          interestAmount: String(data.suggestedInterest ?? 0),
          escrowAmount: String(data.suggestedEscrow ?? 0),
          lateFeeAmount: String(data.suggestedLateFee ?? 0),
        }));
      } catch (e) {
        logger.error(e);
      } finally {
        setIsLoadingBreakdown(false);
        setIsLoading(false);
      }
    },
    [applicationId, setForm, setIsLoading, form.effectiveDate, form.paymentDate],
  );

  const fetchBreakdownRef = useRef(fetchBreakdown);
  fetchBreakdownRef.current = fetchBreakdown;

  const debouncedFetchBreakdown = useMemo(
    () =>
      _.debounce(
        (amount, dueDate, lateFee) => fetchBreakdownRef.current(amount, dueDate, lateFee),
        BREAKDOWN_DEBOUNCE_MS,
      ),
    [],
  );

  useEffect(() => () => debouncedFetchBreakdown.cancel(), [debouncedFetchBreakdown]);

  const onDueDateChange = useCallback(
    (_, selectedOption) => {
      if (!selectedOption) {
        setInForm('dueDate', null);

        return;
      }

      setMultipleInForm({
        dueDate: selectedOption.value,
        totalAmount: String(selectedOption.totalDue),
        paymentDate: selectedOption.value,
        effectiveDate: selectedOption.value,
      });

      fetchBreakdown(String(selectedOption.totalDue), selectedOption.value, '0');
    },
    [setInForm, setMultipleInForm, fetchBreakdown],
  );

  const selectedDueDateOption = useMemo(
    () => unpaidScheduleOptions.find(option => option.value === form.dueDate) ?? null,
    [unpaidScheduleOptions, form.dueDate],
  );

  const onTotalAmountChange = useCallback(
    e => {
      const { value } = e.target;
      setInForm('totalAmount', value);

      if (!form.dueDate || !value) {
        return;
      }

      debouncedFetchBreakdown(value, form.dueDate, form.lateFeeAmount);
    },
    [setInForm, form.dueDate, form.lateFeeAmount, debouncedFetchBreakdown],
  );

  const allocationSum = useMemo(
    () =>
      Math.round(
        (parseAmount(form.principalAmount) +
          parseAmount(form.interestAmount) +
          parseAmount(form.escrowAmount) +
          parseAmount(form.lateFeeAmount) +
          parseAmount(form.otherAmount) +
          parseAmount(form.suspenseAmount)) *
          PERCENTAGE,
      ) / PERCENTAGE,
    [
      form.principalAmount,
      form.interestAmount,
      form.escrowAmount,
      form.lateFeeAmount,
      form.otherAmount,
      form.suspenseAmount,
    ],
  );

  const totalAmount = parseAmount(form.totalAmount);
  const allocationDifference = Math.round((totalAmount - allocationSum) * PERCENTAGE) / PERCENTAGE;
  const isAllocationBalanced = !form.totalAmount || allocationDifference === 0;

  useEffect(() => {
    setIsSavingAllowed(isAllocationBalanced);
  }, [isAllocationBalanced, setIsSavingAllowed]);

  const onSave = useCallback(async () => {
    const isValid = await validateForm({ noFormChangedValidate: true });

    if (!isValid) {
      return;
    }

    setIsLoading(true);

    const body = buildPaymentPayload(form);

    try {
      if (isEditing) {
        await dispatch(
          updateServicingPaymentAction({
            applicationId,
            paymentId: payment.id,
            ...body,
          }),
        );
      }

      if (!isEditing) {
        await dispatch(
          createServicingPaymentAction({
            applicationId,
            ...body,
          }),
        );
      }

      onSuccess();
    } catch (e) {
      logger.error(e);
    } finally {
      setIsLoading(false);
    }
  }, [form, isEditing, dispatch, applicationId, payment, onSuccess, setIsLoading, validateForm]);

  useEffect(() => {
    setOnSave(() => onSave);
  }, [setOnSave, onSave]);

  const showFields = isEditing || !!form.dueDate;

  return (
    <Box sx={formFieldsStyles}>
      <AutocompleteCDS
        required
        dataTestId={PaymentHistoryTestIds.DUE_DATE_SELECT}
        error={!!validationErrors.dueDate}
        helperText={validationErrors.dueDate}
        label="Due Date"
        name="dueDate"
        optionKey="value"
        optionName="name"
        options={unpaidScheduleOptions}
        value={selectedDueDateOption}
        onChange={onDueDateChange}
      />

      {showFields && (
        <>
          <Box sx={rowStyles}>
            <DateFieldCDS
              required
              dataTestId={PaymentHistoryTestIds.PAYMENT_DATE}
              error={!!validationErrors.paymentDate}
              helperText={validationErrors.paymentDate}
              label="Payment Date"
              name="paymentDate"
              value={form.paymentDate}
              onChange={onPaymentDateChange}
            />
            <DateFieldCDS
              required
              dataTestId={PaymentHistoryTestIds.EFFECTIVE_DATE}
              error={!!validationErrors.effectiveDate}
              helperText={validationErrors.effectiveDate}
              label="Effective Date"
              name="effectiveDate"
              value={form.effectiveDate}
              onChange={onEffectiveDateChange}
            />
          </Box>

          <DataFieldCDS
            required
            disabled={isSaving || isLoadingBreakdown}
            error={!!validationErrors.totalAmount}
            helperText={validationErrors.totalAmount}
            label="Total Amount"
            name="totalAmount"
            type={FieldInputType.CURRENCY}
            value={form.totalAmount}
            onChange={onTotalAmountChange}
          />

          <Box sx={rowStyles}>
            <DataFieldCDS
              disabled={isSaving || isLoadingBreakdown}
              label="Principal"
              name="principalAmount"
              type={FieldInputType.CURRENCY}
              value={form.principalAmount}
              onChange={handleChange}
            />
            <DataFieldCDS
              disabled={isSaving || isLoadingBreakdown}
              label="Interest"
              name="interestAmount"
              type={FieldInputType.CURRENCY}
              value={form.interestAmount}
              onChange={handleChange}
            />
            <DataFieldCDS
              disabled={isSaving || isLoadingBreakdown}
              label="Escrow"
              name="escrowAmount"
              type={FieldInputType.CURRENCY}
              value={form.escrowAmount}
              onChange={handleChange}
            />
            <DataFieldCDS
              disabled={isSaving || isLoadingBreakdown}
              label="Late Fee"
              name="lateFeeAmount"
              type={FieldInputType.CURRENCY}
              value={form.lateFeeAmount}
              onChange={handleChange}
            />
          </Box>

          <Box sx={rowStyles}>
            <DataFieldCDS
              disabled={isSaving || isLoadingBreakdown}
              label="Other"
              name="otherAmount"
              type={FieldInputType.CURRENCY}
              value={form.otherAmount}
              onChange={handleChange}
            />
            <DataFieldCDS
              disabled={isSaving || isLoadingBreakdown}
              label="Suspense"
              name="suspenseAmount"
              type={FieldInputType.CURRENCY}
              value={form.suspenseAmount}
              onChange={handleChange}
            />
          </Box>

          {!isAllocationBalanced && !isLoadingBreakdown && !!form.totalAmount && (
            <Box sx={warningBoxStyles}>
              <Typography color="warning.main" variant="regular14">
                Allocation does not equal Total Amount. Difference: $
                {Math.abs(allocationDifference).toFixed(2)}
                {allocationDifference > 0 ? ' (under-allocated)' : ' (over-allocated)'}
              </Typography>
              <Typography color="text.secondary" variant="regular12">
                Allocated: ${allocationSum.toFixed(2)} / Total: ${totalAmount.toFixed(2)}
              </Typography>
            </Box>
          )}

          <Box sx={rowStyles}>
            <AutocompleteCDS
              required
              dataTestId={PaymentHistoryTestIds.METHOD_SELECT}
              error={!!validationErrors.paymentMethod}
              helperText={validationErrors.paymentMethod}
              label="Payment Method"
              name="paymentMethod"
              optionKey="value"
              optionName="name"
              options={PAYMENT_METHOD_OPTIONS}
              value={form.paymentMethod}
              onChange={onMethodChange}
            />
            <AutocompleteCDS
              required
              dataTestId={PaymentHistoryTestIds.STATUS_SELECT}
              error={!!validationErrors.paymentStatus}
              helperText={validationErrors.paymentStatus}
              label="Payment Status"
              name="paymentStatus"
              optionKey="value"
              optionName="name"
              options={PAYMENT_STATUS_OPTIONS}
              value={form.paymentStatus}
              onChange={onStatusChange}
            />
          </Box>

          <TextFieldCDS
            dataTestId={PaymentHistoryTestIds.REFERENCE_NUMBER_INPUT}
            disabled={isSaving}
            label="Reference Number"
            name="referenceNumber"
            value={form.referenceNumber}
            onChange={handleChange}
          />

          <TextFieldCDS
            multiline
            dataTestId={PaymentHistoryTestIds.NOTES_INPUT}
            disabled={isSaving}
            label="Notes"
            name="notes"
            rows={3}
            value={form.notes}
            onChange={handleChange}
          />
        </>
      )}
    </Box>
  );
};

PaymentDrawerContent.propTypes = {
  additionalProps: PropTypes.shape({
    applicationId: PropTypes.string.isRequired,
    onSuccess: PropTypes.func.isRequired,
    payment: PropTypes.object,
    paymentSchedule: PropTypes.array,
  }).isRequired,
  setIsFormChanged: PropTypes.func.isRequired,
  setIsLoading: PropTypes.func.isRequired,
  setIsSavingAllowed: PropTypes.func.isRequired,
  setOnSave: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
};

export default memo(PaymentDrawerContent);
